import type { ResponseErrorConfig } from "@dc/gateway-jsonapi";
import type { MutationCacheNotifyEvent } from "@tanstack/react-query";

export const EMS_QUOTA_ERROR_CODES = ["EMS_PROJECT_LIMIT_REACHED"] as const;

export type EmsQuotaErrorCode = (typeof EMS_QUOTA_ERROR_CODES)[number];

type JsonApiError = { code?: string };
type JsonApiErrors = { errors?: Array<JsonApiError> };

function isEmsQuotaErrorCode(code: string | undefined): code is EmsQuotaErrorCode {
  return !!code && (EMS_QUOTA_ERROR_CODES as readonly string[]).includes(code);
}

/**
 * Inspect a raw error (e.g. a rejected mutation error) and return the EMS quota code if present.
 * The API surfaces this code at the top-level `errors[].code` field
 * (see https://jsonapi.org/format/#error-objects — emitted by EmsQuotaForbiddenException).
 * Mirrors getTrialErrorCodeFromError (lib/trialError.ts) — kept separate rather than
 * merged into it since the two cases need different UI (no sales CTA here, just a
 * dismissible message), not just an extra error code on a shared modal.
 */
export function getEmsQuotaErrorCodeFromError(err: unknown): EmsQuotaErrorCode | null {
  const error = err as ResponseErrorConfig<JsonApiErrors> | undefined;
  const errors = error?.response?.data?.errors;
  if (!errors) return null;

  for (const entry of errors) {
    if (isEmsQuotaErrorCode(entry.code)) return entry.code;
  }

  return null;
}

export function getEmsQuotaErrorCode(event: MutationCacheNotifyEvent): EmsQuotaErrorCode | null {
  if (event.type !== "updated") return null;
  if (event.action.type !== "error") return null;
  return getEmsQuotaErrorCodeFromError(event.action.error);
}
